import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UserCog, Plus, Trash2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

export const TeamMembersCard = () => {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMembers = async () => {
    try {
      const res = await api.get("/admin/team-members");
      setMembers(res.data.team_members || []);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim() || !createEmail.trim()) { toast.error("Enter a name and email."); return; }
    setCreating(true);
    try {
      const res = await api.post("/admin/team-members/create", {
        name: createName.trim(),
        email: createEmail.trim(),
        origin_url: window.location.origin,
      });
      toast.success(`Invite sent to ${res.data.email}. They'll set their password from the email.`);
      setCreateName("");
      setCreateEmail("");
      fetchMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create team member.");
    } finally {
      setCreating(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter an email address."); return; }
    setAdding(true);
    try {
      const res = await api.post("/admin/team-members", { email: email.trim() });
      toast.success(`${res.data.name || res.data.email} is now a team member.`);
      setEmail("");
      fetchMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add team member.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} as a team member? They'll lose free access and can no longer manage their partners (their partners remain).`)) return;
    try {
      await api.delete(`/admin/team-members/${m.user_id}`);
      toast.success("Team member removed.");
      fetchMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove team member.");
    }
  };

  return (
    <Card data-testid="team-members-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <UserCog className="w-5 h-5 text-[#547d91]" />
          Internal Team Members
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Team members get free AI Buyers Agent access and can create/manage their own referral partners. Add an existing user by email.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create a brand-new team member and email them a set-password invite */}
        <form onSubmit={handleCreate} className="space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="create-team-member-form">
          <p className="text-sm font-medium text-slate-700">Create &amp; invite a new team member</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="text"
              placeholder="Full name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="sm:max-w-[220px]"
              data-testid="create-team-member-name-input"
            />
            <Input
              type="email"
              placeholder="name@example.com"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
              className="sm:max-w-sm"
              data-testid="create-team-member-email-input"
            />
            <Button type="submit" className="bg-[#547d91] hover:bg-[#456a7a]" disabled={creating} data-testid="create-team-member-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4 mr-2" /> Create &amp; send invite</>}
            </Button>
          </div>
          <p className="text-xs text-slate-500">Creates the account and emails them a secure link to set their own password.</p>
        </form>

        {/* Promote an already-registered user */}
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2 items-start">
          <div className="flex-1 w-full">
            <p className="text-sm font-medium text-slate-700 mb-2">Or add an existing user by email</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="existing-user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sm:max-w-sm"
                data-testid="team-member-email-input"
              />
              <Button type="submit" variant="outline" disabled={adding} data-testid="add-team-member-btn">
                {adding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</> : <><Plus className="w-4 h-4 mr-2" /> Add existing user</>}
              </Button>
            </div>
          </div>
        </form>

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-slate-400">No team members yet. Add one above.</p>
        ) : (
          <div className="space-y-2" data-testid="team-members-list">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`team-member-${m.user_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{m.name || "—"}</p>
                  <p className="text-sm text-slate-500 truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="secondary" className="text-xs">{m.partner_count || 0} partners</Badge>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleRemove(m)} data-testid={`remove-team-member-${m.user_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
